import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, bandasAPI, cofradiasAPI } from '../services/api';
import { markLoginAlertsPending, clearLoginAlertsPending } from '../utils/loginAlerts';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // During Vite HMR, components can briefly render outside the provider.
    // Return a safe default instead of throwing to avoid crash on hot reload.
    return {
      user: null,
      profile: null,
      loading: true,
      error: null,
      isAuthenticated: false,
      isBanda: false,
      isBandaAccount: false,
      isTitularBanda: false,
      isCofradia: false,
      isAdmin: false,
      plan: null,
      features: {},
      hasFeature: () => true,
      login: async () => ({ success: false, error: 'AuthProvider not ready' }),
      register: async () => ({ success: false, error: 'AuthProvider not ready' }),
      logout: () => {},
      updateProfile: async () => ({ success: false, error: 'AuthProvider not ready' }),
      uploadAvatar: async () => ({ success: false, error: 'AuthProvider not ready' }),
      loadProfile: async () => null,
      refreshUser: async () => null,
      forgotPassword: async () => ({ success: false, error: 'AuthProvider not ready' }),
      resetPassword: async () => ({ success: false, error: 'AuthProvider not ready' }),
      clearError: () => {},
      showPostLoginSplash: false,
      completePostLoginSplash: () => {},
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** Splash corporativo tras login exitoso (AuthRoute lo muestra antes de redirigir). */
  const [showPostLoginSplash, setShowPostLoginSplash] = useState(false);

  // Cargar usuario del localStorage al iniciar
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const accessToken = localStorage.getItem('accessToken');

      if (storedUser && accessToken) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);

          // Refrescar datos del usuario desde el servidor (incluye username, etc.)
          try {
            const meResponse = await authAPI.getMe();
            const freshUser = {
              ...parsedUser,
              username: meResponse.data.username,
              usernameChangesThisYear: meResponse.data.usernameChangesThisYear,
              usernameLastChangedAt: meResponse.data.usernameLastChangedAt,
              gestionPermissions: meResponse.data.gestionPermissions ?? parsedUser.gestionPermissions,
              plan: meResponse.data.plan ?? parsedUser.plan ?? null,
            };
            setUser(freshUser);
            localStorage.setItem('user', JSON.stringify(freshUser));
          } catch (meErr) {
            console.warn('Could not refresh user data from /auth/me:', meErr);
          }

          // Obtener perfil actualizado
          await loadProfile(parsedUser.userType);
        } catch (err) {
          console.error('Error loading user:', err);
          // Solo hacer logout si el token es inválido (401), no por otros errores
          if (err?.response?.status === 401) {
            logout();
          }
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  // Tras login/registro, la respuesta puede no traer el plan efectivo; lo
  // pedimos a /auth/me y lo fusionamos en el usuario (no bloqueante).
  const enrichPlanFromMe = useCallback(async (baseUser) => {
    try {
      const meResponse = await authAPI.getMe();
      const merged = {
        ...baseUser,
        plan: meResponse.data.plan ?? baseUser.plan ?? null,
        gestionPermissions:
          meResponse.data.gestionPermissions ?? baseUser.gestionPermissions,
      };
      setUser(merged);
      localStorage.setItem('user', JSON.stringify(merged));
    } catch {
      /* noop: el plan se resolverá en el siguiente arranque */
    }
  }, []);

  // Recarga el usuario desde /auth/me (p. ej. tras cambiar de plan) para
  // refrescar plan/features/gestionPermissions en toda la app.
  const refreshUser = useCallback(async () => {
    try {
      const meResponse = await authAPI.getMe();
      setUser((prev) => {
        const merged = {
          ...(prev || {}),
          plan: meResponse.data.plan ?? prev?.plan ?? null,
          gestionPermissions:
            meResponse.data.gestionPermissions ?? prev?.gestionPermissions,
        };
        localStorage.setItem('user', JSON.stringify(merged));
        return merged;
      });
      return meResponse.data;
    } catch {
      return null;
    }
  }, []);

  const loadProfile = useCallback(async (userType) => {
    try {
      let profileData = null;
      if (userType === 'BANDA' || userType === 'BANDA_COLABORADOR') {
        const response = await bandasAPI.getMyProfile();
        profileData = response.data;
      } else if (userType === 'COFRADIA') {
        const response = await cofradiasAPI.getMyProfile();
        profileData = response.data;
      }
      // Admin no tiene perfil de banda/cofradía
      setProfile(profileData);
      return profileData;
    } catch (err) {
      console.error('Error loading profile:', err);
      return null;
    }
  }, []);

  const login = async (email, password) => {
    setError(null);

    try {
      const response = await authAPI.login({ email, password });
      const { user: userData, accessToken, refreshToken } = response.data;

      // Guardar tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      if (userData.userType === 'BANDA' || userData.userType === 'COFRADIA') {
        markLoginAlertsPending();
      }

      setShowPostLoginSplash(true);
      setUser(userData);

      // Enriquecer con el plan efectivo desde /auth/me.
      enrichPlanFromMe(userData);

      // Cargar perfil
      await loadProfile(userData.userType);

      return { success: true, user: userData };
    } catch (err) {
      const raw = err.response?.data?.message;
      const message = Array.isArray(raw) ? raw.join(' ') : raw || 'Error al iniciar sesión';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    console.log('🔐 AuthContext: register llamado con:', data);
    setError(null);

    try {
      console.log('📡 AuthContext: Llamando a authAPI.register...');
      const response = await authAPI.register(data);
      console.log('✅ AuthContext: Respuesta exitosa:', response.data);
      const { user: userData, accessToken, refreshToken } = response.data;

      // Guardar tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);

      // Enriquecer con el plan efectivo desde /auth/me.
      enrichPlanFromMe(userData);

      // Cargar perfil
      await loadProfile(userData.userType);

      return { success: true, user: userData };
    } catch (err) {
      console.log('❌ AuthContext: Error capturado:', err);
      console.log('📋 AuthContext: err.response:', err.response);
      const message = err.response?.data?.message || 'Error al registrarse';
      console.log('💬 AuthContext: Mensaje de error:', message);
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = useCallback(() => {
    // Llamar a la API de logout
    authAPI.logout().catch(() => {});

    // Limpiar localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Limpiar estado
    setUser(null);
    setProfile(null);
    setError(null);
    setShowPostLoginSplash(false);
    clearLoginAlertsPending();
  }, []);

  const updateProfile = async (data) => {
    try {
      let response;
      if (user.userType === 'BANDA') {
        response = await bandasAPI.update(data);
      } else if (user.userType === 'BANDA_COLABORADOR') {
        return { success: false, error: 'Las cuentas delegadas no pueden editar el perfil completo de la banda.' };
      } else if (user.userType === 'COFRADIA') {
        response = await cofradiasAPI.update(data);
      }
      setProfile(response.data);
      return { success: true, profile: response.data };
    } catch (err) {
      const message = err.response?.data?.message || 'Error al actualizar perfil';
      return { success: false, error: message };
    }
  };

  const uploadAvatar = async (file) => {
    try {
      let response;
      if (user.userType === 'BANDA') {
        response = await bandasAPI.uploadAvatar(file);
      } else if (user.userType === 'BANDA_COLABORADOR') {
        return { success: false, error: 'Solo el titular de la banda puede cambiar el avatar público.' };
      } else if (user.userType === 'COFRADIA') {
        response = await cofradiasAPI.uploadAvatar(file);
      }
      
      // Recargar perfil
      await loadProfile(user.userType);
      
      return { success: true, url: response.data.url };
    } catch (err) {
      const message = err.response?.data?.message || 'Error al subir avatar';
      return { success: false, error: message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await authAPI.forgotPassword(email);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Error al solicitar cambio de contraseña';
      return { success: false, error: message };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await authAPI.resetPassword(token, newPassword);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Error al restablecer contraseña';
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    isBanda: user?.userType === 'BANDA',
    isBandaAccount: user?.userType === 'BANDA' || user?.userType === 'BANDA_COLABORADOR',
    isTitularBanda: user?.userType === 'BANDA',
    isCofradia: user?.userType === 'COFRADIA',
    isAdmin: user?.userType === 'ADMIN',
    plan: user?.plan ?? null,
    features: user?.plan?.features ?? {},
    // Admin siempre tiene acceso; si aún no hay plan cargado, no bloqueamos (true).
    hasFeature: (key) => {
      if (user?.userType === 'ADMIN') return true;
      const features = user?.plan?.features;
      if (!features) return true;
      return features[key] === true;
    },
    login,
    register,
    logout,
    updateProfile,
    uploadAvatar,
    loadProfile,
    refreshUser,
    forgotPassword,
    resetPassword,
    showPostLoginSplash,
    completePostLoginSplash: () => setShowPostLoginSplash(false),
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;


